import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const URL_PATTERN = /^(https?:\/\/|www\.)/i
const POSSIBLE_DOMAIN_PATTERN = /^[^\s]+\.[^\s]{2,}/
const REQUEST_DEBOUNCE_MS = 280

export default class extends Controller {
  static targets = [
    "input",
    "wishlistSelect",
    "loading",
    "preview",
    "previewImage",
    "previewTitle",
    "previewPrice",
    "previewSource",
    "textAction",
    "textLabel",
    "status",
    "recentlyAdded",
    "recentlyAddedList"
  ]

  static values = {
    metadataUrl: String,
    createUrlTemplate: String,
    defaultWishlistId: Number
  }

  connect() {
    this.previewPayload = null
    this.recentlyAdded = []
    this.requestToken = 0
    this.debounceTimer = null
    this.resetUi()
  }

  disconnect() {
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer)
  }

  onInput() {
    const value = this.inputValue
    if (value === "") {
      this.requestToken += 1
      this.previewPayload = null
      this.renderState("empty")
      return
    }

    if (this.looksLikeUrl(value)) {
      this.renderState("loading")
      this.queueMetadataFetch(value)
      return
    }

    this.requestToken += 1
    this.previewPayload = null
    this.renderTextAction(value)
    this.renderState("text")
  }

  onKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.hasLoadingTarget && !this.loadingTarget.hidden) return

      if (this.previewPayload) {
        this.addFromPreview()
      } else if (this.inputValue !== "") {
        this.addFromText()
      }
    }
  }

  done(event) {
    event.preventDefault()

    if (this.recentlyAdded.length > 0) {
      Turbo.visit(window.location.href)
      return
    }

    this.closeModal()
  }

  onModalOpened() {
    if (!this.hasInputTarget) return

    try {
      this.inputTarget.focus({ preventScroll: true })
    } catch (_error) {
      this.inputTarget.focus()
    }
  }

  onModalClosed() {
    this.previewPayload = null
    this.recentlyAdded = []
    this.requestToken += 1
    this.resetUi()
  }

  addFromPreview(event) {
    event?.preventDefault()
    if (!this.previewPayload) return

    const payload = {
      title: this.previewPayload.title || this.inputValue,
      url: this.previewPayload.url || this.inputValue,
      description: this.previewPayload.description || ""
    }

    // Include price if extracted by AI
    if (this.previewPayload.price) {
      payload.price = this.parsePrice(this.previewPayload.price)
    }

    this.createItem(payload)
  }

  addFromText(event) {
    event?.preventDefault()
    const value = this.inputValue
    if (value === "") return

    const payload = { title: value }
    if (this.looksLikeUrl(value)) payload.url = value

    this.createItem(payload)
  }

  queueMetadataFetch(value) {
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer)

    const currentToken = ++this.requestToken
    this.debounceTimer = window.setTimeout(() => {
      this.fetchMetadata(value, currentToken)
    }, REQUEST_DEBOUNCE_MS)
  }

  async fetchMetadata(rawValue, token) {
    if (!this.hasMetadataUrlValue) return

    try {
      const response = await fetch(this.metadataUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ url: rawValue })
      })

      const payload = await response.json()
      if (token !== this.requestToken) return

      if (!response.ok || payload.error) {
        this.previewPayload = null
        this.renderTextAction(this.inputValue)
        this.renderState("text")
        return
      }

      this.previewPayload = payload
      this.renderPreview(payload)
      this.renderState("url")
    } catch (_error) {
      if (token !== this.requestToken) return
      this.previewPayload = null
      this.renderTextAction(this.inputValue)
      this.renderState("text")
    }
  }

  async createItem(itemPayload) {
    const wishlistId = this.selectedWishlistId
    const endpoint = this.createEndpointFor(wishlistId)
    if (!endpoint) return

    this.setStatus(this.statusMessages.adding)

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ item: itemPayload })
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload.message || this.statusMessages.error)
      }

      this.pushRecentlyAdded(payload.title || itemPayload.title)
      this.inputTarget.value = ""
      this.previewPayload = null
      this.renderState("empty")
      this.setStatus(this.statusMessages.added)
      this.inputTarget.focus()
    } catch (error) {
      this.setStatus(error.message || this.statusMessages.error)
    }
  }

  renderState(mode) {
    this.toggleTarget("loading", mode === "loading")
    this.toggleTarget("preview", mode === "url" && this.previewPayload)
    this.toggleTarget("textAction", mode === "text" && this.inputValue !== "")
    this.toggleTarget("recentlyAdded", this.recentlyAdded.length > 0)

    if (this.hasInputTarget) {
      this.inputTarget.classList.toggle("is-url-success", mode === "url")
      this.inputTarget.classList.toggle("is-url-loading", mode === "loading")
    }
  }

  renderPreview(payload) {
    if (this.hasPreviewTitleTarget) this.previewTitleTarget.textContent = payload.title || this.inputValue
    if (this.hasPreviewSourceTarget) this.previewSourceTarget.textContent = payload.host || ""

    if (this.hasPreviewPriceTarget) {
      if (payload.price) {
        this.previewPriceTarget.textContent = payload.price
        this.previewPriceTarget.classList.remove("is-empty")
      } else {
        this.previewPriceTarget.textContent = this.statusMessages.noPrice
        this.previewPriceTarget.classList.add("is-empty")
      }
    }

    if (this.hasPreviewImageTarget) {
      const hasImage = payload.image && /^https?:\/\//.test(payload.image)
      this.previewImageTarget.innerHTML = hasImage ?
        `<img src="${this.escapeAttribute(payload.image)}" alt="" class="smart-add-preview-thumb">` :
        "🎁"
    }
  }

  renderTextAction(value) {
    if (!this.hasTextLabelTarget) return
    this.textLabelTarget.textContent = value
  }

  resetUi() {
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.renderState("empty")
    this.setStatus(this.statusMessages.batchHint)
  }

  pushRecentlyAdded(title) {
    this.recentlyAdded.unshift(title)
    this.recentlyAdded = this.recentlyAdded.slice(0, 5)

    if (!this.hasRecentlyAddedListTarget) return
    this.recentlyAddedListTarget.innerHTML = this.recentlyAdded
      .map((entry) => this.recentlyAddedMarkup(entry))
      .join("")
  }

  recentlyAddedMarkup(title) {
    return `
      <div class="recently-added-item">
        <div class="recently-added-item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
        </div>
        <span class="recently-added-item-name">${this.escapeHtml(title)}</span>
      </div>
    `
  }

  closeModal() {
    const closeButton = this.element.closest(".modal-panel")?.querySelector(".modal-close")
    closeButton?.click()
  }

  createEndpointFor(wishlistId) {
    if (!this.hasCreateUrlTemplateValue) return null

    return this.createUrlTemplateValue.replace("__WISHLIST_ID__", String(wishlistId))
  }

  looksLikeUrl(value) {
    return URL_PATTERN.test(value) || (POSSIBLE_DOMAIN_PATTERN.test(value) && !/\s/.test(value))
  }

  toggleTarget(name, visible) {
    const key = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    if (!this[key]) return
    this[`${name}Target`].hidden = !visible
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
  }

  escapeHtml(value) {
    return value.toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  escapeAttribute(value) {
    return this.escapeHtml(value)
  }

  parsePrice(priceString) {
    if (!priceString) return null
    // Extract numeric value from price string (e.g., "€349.00" -> "349.00")
    const match = priceString.toString().match(/[\d.,]+/)
    if (!match) return null
    // Normalize: replace comma with dot for decimal, remove thousand separators
    let value = match[0]
    // Handle European format (1.234,56) vs US format (1,234.56)
    if (value.includes(",") && value.includes(".")) {
      // If comma comes after dot, it's the decimal separator (European)
      if (value.lastIndexOf(",") > value.lastIndexOf(".")) {
        value = value.replace(/\./g, "").replace(",", ".")
      } else {
        // US format
        value = value.replace(/,/g, "")
      }
    } else if (value.includes(",")) {
      // Could be decimal separator or thousand separator
      // If only one comma and 2 digits after, treat as decimal
      const parts = value.split(",")
      if (parts.length === 2 && parts[1].length <= 2) {
        value = value.replace(",", ".")
      } else {
        value = value.replace(/,/g, "")
      }
    }
    return value
  }

  get inputValue() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : ""
  }

  get selectedWishlistId() {
    if (this.hasWishlistSelectTarget && this.wishlistSelectTarget.value) {
      return this.wishlistSelectTarget.value
    }

    return this.defaultWishlistIdValue || ""
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  get statusMessages() {
    return {
      batchHint: this.element.dataset.smartAddBatchHint || "Add multiple items quickly. Press Enter to add.",
      adding: this.element.dataset.smartAddAdding || "Adding item…",
      added: this.element.dataset.smartAddAdded || "Item added. You can add another one.",
      error: this.element.dataset.smartAddError || "Could not add item. Please try again.",
      noPrice: this.element.dataset.smartAddNoPrice || "No price detected"
    }
  }
}
